import React, { useState, useContext } from 'react';
import { Calendar, FileText, BarChart2, MessageSquare, Bell, Send, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DraggableGrid } from '../ui/DraggableGrid';
import { LeaveBalanceWidget } from '../widgets/LeaveBalanceWidget';
import { UpcomingLeavesWidget } from '../widgets/UpcomingLeavesWidget';
import { RequestsWidget } from '../widgets/RequestsWidget';
import { EvaluationsWidget } from '../widgets/EvaluationsWidget';
import { OnboardingTasksWidget } from '../widgets/OnboardingTasksWidget';
import { ActiveSurveyWidget } from '../widgets/ActiveSurveyWidget';
import { PerformanceWidget } from '../widgets/PerformanceWidget';
import { FeedbackWidget } from '../widgets/FeedbackWidget';
import { Modal } from '../ui/Modal';
import { useToast } from '../../context/ToastContext';
import { UserContext } from '../../context/UserContext';
export function EmployeeDashboard() {
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const {
    userName
  } = useContext(UserContext);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isFeedbackRequestModalOpen, setIsFeedbackRequestModalOpen] = useState(false);
  // États pour le feedback
  const [feedbackRecipient, setFeedbackRecipient] = useState('');
  const [feedbackType, setFeedbackType] = useState<'positive' | 'constructive'>('positive');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const widgets = [{
    id: 'feedback',
    component: <FeedbackWidget onRequestFeedback={() => setIsFeedbackRequestModalOpen(true)} data-id="element-136" />
  }, {
    id: 'leave-balance',
    component: <LeaveBalanceWidget data-id="element-137" />
  }, {
    id: 'upcoming-leaves',
    component: <UpcomingLeavesWidget data-id="element-138" />
  }, {
    id: 'requests',
    component: <RequestsWidget data-id="element-139" />
  }, {
    id: 'evaluations',
    component: <EvaluationsWidget data-id="element-140" />
  }, {
    id: 'onboarding-tasks',
    component: <OnboardingTasksWidget data-id="element-141" />
  }, {
    id: 'active-survey',
    component: <ActiveSurveyWidget data-id="element-142" />
  }, {
    id: 'performance',
    component: <PerformanceWidget data-id="element-143" />
  }];
  const quickActions = [{
    icon: Calendar,
    label: 'Demander un congé',
    action: () => setIsLeaveModalOpen(true),
    color: 'blue'
  }, {
    icon: FileText,
    label: 'Mes évaluations',
    action: () => setIsEvaluationModalOpen(true),
    color: 'purple'
  }, {
    icon: BarChart2,
    label: 'Sondages',
    action: () => setIsSurveyModalOpen(true),
    color: 'green'
  }, {
    icon: MessageSquare,
    label: 'Donner du feedback',
    action: () => setIsFeedbackModalOpen(true),
    color: 'amber'
  }];
  const teamMembers = ['Thomas Petit', 'Sophie Bernard', 'Luc Tremblay', 'Jean Martin'];
  const handleLeaveSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLeaveModalOpen(false);
    showToast('Demande de congé soumise avec succès', 'success');
  };
  const handleFeedbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackRecipient || !feedbackMessage.trim()) {
      showToast('Veuillez remplir tous les champs', 'error');
      return;
    }
    // Simuler l'envoi du feedback pour validation
    setIsFeedbackModalOpen(false);
    setFeedbackRecipient('');
    setFeedbackMessage('');
    setFeedbackType('positive');
    showToast('Feedback envoyé au gestionnaire pour validation', 'success');
  };
  const handleFeedbackRequestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFeedbackRequestModalOpen(false);
    showToast('Demande de feedback envoyée avec succès', 'success');
  };
  return <div className="pb-20 md:pb-0" data-id="element-144">
      {/* News Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-145">
        <div className="flex items-start gap-3" data-id="element-146">
          <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-147" />
          <div data-id="element-148">
            <h3 className="font-semibold text-blue-900 mb-1" data-id="element-149">
              Nouvelles importantes
            </h3>
            <p className="text-sm text-blue-700 leading-relaxed" data-id="element-150">
              Nouveau sondage disponible • Évaluation annuelle à compléter avant
              le 15 déc.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Quick Actions */}
      <div className="md:hidden mb-6" data-id="element-151">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-152">
          Actions rapides
        </h2>
        <div className="grid grid-cols-2 gap-3" data-id="element-153">
          {quickActions.map(action => {
          const Icon = action.icon;
          return <button key={action.label} onClick={action.action} className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all" data-id="element-154">
                <Icon className="h-7 w-7 text-blue-600 mb-2 mx-auto" data-id="element-155" />
                <span className="text-sm font-medium text-gray-900 block text-center" data-id="element-156">
                  {action.label}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:block" data-id="element-157">
        <DraggableGrid items={widgets} storageKey="employee-dashboard-layout" columns={3} data-id="element-158" />
      </div>

      {/* Mobile Widgets */}
      <div className="md:hidden space-y-4" data-id="element-159">
        <FeedbackWidget onRequestFeedback={() => setIsFeedbackRequestModalOpen(true)} data-id="element-160" />
        <LeaveBalanceWidget data-id="element-161" />
        <PerformanceWidget data-id="element-162" />
        <EvaluationsWidget data-id="element-163" />
        <ActiveSurveyWidget data-id="element-164" />
      </div>

      {/* Leave Request Modal */}
      <Modal isOpen={isLeaveModalOpen} onClose={() => setIsLeaveModalOpen(false)} title="Demander un congé" size="md" data-id="element-165">
        <form onSubmit={handleLeaveSubmit} className="space-y-4" data-id="element-166">
          <div data-id="element-167">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-168">
              Type de congé
            </label>
            <select className="w-full px-4 py-2.5 text-base rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-169">
              <option data-id="element-170">Congés payés</option>
              <option data-id="element-171">RTT</option>
              <option data-id="element-172">Maladie</option>
              <option data-id="element-173">Congé sans solde</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-174">
            <div data-id="element-175">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-176">
                Date de début
              </label>
              <div className="relative" data-id="element-177">
                <input type="date" required className="w-full px-4 py-2.5 text-base rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-178" />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" data-id="element-179" />
              </div>
            </div>
            <div data-id="element-180">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-181">
                Date de fin
              </label>
              <div className="relative" data-id="element-182">
                <input type="date" required className="w-full px-4 py-2.5 text-base rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors" data-id="element-183" />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" data-id="element-184" />
              </div>
            </div>
          </div>
          <div data-id="element-185">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-186">
              Commentaire (optionnel)
            </label>
            <textarea rows={3} className="w-full px-4 py-2.5 text-base rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors resize-none" data-id="element-187" />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4" data-id="element-188">
            <button type="button" onClick={() => setIsLeaveModalOpen(false)} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-189">
              Annuler
            </button>
            <button type="submit" className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors" data-id="element-190">
              Soumettre
            </button>
          </div>
        </form>
      </Modal>

      {/* Evaluation Modal */}
      <Modal isOpen={isEvaluationModalOpen} onClose={() => setIsEvaluationModalOpen(false)} title="Évaluation annuelle 2023" size="md" data-id="element-191">
        <div className="space-y-4" data-id="element-192">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-193">
            <h3 className="font-medium text-blue-900 mb-2" data-id="element-194">Progression: 40%</h3>
            <div className="w-full bg-blue-200 rounded-full h-2 mb-3" data-id="element-195">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{
              width: '40%'
            }} data-id="element-196"></div>
            </div>
            <p className="text-sm text-blue-700" data-id="element-197">2/5 sections complétées</p>
          </div>
          <div className="space-y-2" data-id="element-198">
            {[{
            label: 'Auto-évaluation',
            completed: true
          }, {
            label: 'Objectifs',
            completed: true
          }, {
            label: 'Compétences',
            completed: false
          }, {
            label: 'Développement',
            completed: false
          }, {
            label: 'Commentaires',
            completed: false
          }].map((section, index) => <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${section.completed ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-200'}`} data-id="element-199">
                <span className="text-sm font-medium text-gray-900" data-id="element-200">
                  {section.label}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${section.completed ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`} data-id="element-201">
                  {section.completed ? 'Complété' : 'À faire'}
                </span>
              </div>)}
          </div>
          <div className="flex justify-end gap-3 pt-4" data-id="element-202">
            <button onClick={() => setIsEvaluationModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-203">
              Fermer
            </button>
            <button onClick={() => {
            setIsEvaluationModalOpen(false);
            navigate('/evaluations');
          }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-204">
              Continuer l'évaluation
            </button>
          </div>
        </div>
      </Modal>

      {/* Survey Modal */}
      <Modal isOpen={isSurveyModalOpen} onClose={() => setIsSurveyModalOpen(false)} title="Sondage actif" size="md" data-id="element-205">
        <div className="space-y-4" data-id="element-206">
          <div className="bg-blue-50 rounded-lg p-5 border border-blue-100" data-id="element-207">
            <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-208">
              Pulse bien-être Q4 2023
            </h3>
            <p className="text-sm text-gray-700 mb-3" data-id="element-209">
              Partagez votre ressenti sur votre environnement de travail
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-600" data-id="element-210">
              <span className="flex items-center gap-1" data-id="element-211">
                📅 Fermeture: 18 nov. 2023
              </span>
              <span className="flex items-center gap-1" data-id="element-212">⏱️ Durée: 15 min</span>
              <span className="flex items-center gap-1" data-id="element-213">🔒 Anonyme</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-214">
            <h4 className="font-medium text-gray-900 mb-2" data-id="element-215">
              À propos de ce sondage
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-216">
              Ce sondage nous aide à comprendre votre bien-être au travail et à
              identifier les domaines d'amélioration. Vos réponses sont
              totalement anonymes.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4" data-id="element-217">
            <button onClick={() => setIsSurveyModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-218">
              Plus tard
            </button>
            <button onClick={() => {
            setIsSurveyModalOpen(false);
            navigate('/sondages');
          }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-219">
              Répondre maintenant
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de feedback amélioré avec validation */}
      <Modal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)} title="Envoyer du feedback à un collègue" size="lg" data-id="element-220">
        <form onSubmit={handleFeedbackSubmit} className="space-y-6" data-id="element-221">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-222">
            <div className="flex items-start gap-3" data-id="element-223">
              <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-224" />
              <div data-id="element-225">
                <h3 className="font-semibold text-blue-900 mb-1" data-id="element-226">
                  Feedback entre collègues
                </h3>
                <p className="text-sm text-blue-700 leading-relaxed" data-id="element-227">
                  Votre feedback sera envoyé à votre gestionnaire pour
                  validation avant d'être transmis à votre collègue. Cela
                  garantit un environnement de travail respectueux et
                  constructif.
                </p>
              </div>
            </div>
          </div>

          <div data-id="element-228">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-229">
              Destinataire
            </label>
            <select value={feedbackRecipient} onChange={e => setFeedbackRecipient(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required data-id="element-230">
              <option value="" data-id="element-231">Sélectionner un collègue...</option>
              {teamMembers.map(member => <option key={member} value={member} data-id="element-232">
                  {member}
                </option>)}
            </select>
          </div>

          <div data-id="element-233">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-234">
              Type de feedback
            </label>
            <div className="flex gap-3" data-id="element-235">
              <button type="button" onClick={() => setFeedbackType('positive')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feedbackType === 'positive' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`} data-id="element-236">
                <ThumbsUp className={`h-5 w-5 mx-auto mb-1 ${feedbackType === 'positive' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-237" />
                <span className={`text-sm font-medium ${feedbackType === 'positive' ? 'text-green-900' : 'text-gray-600'}`} data-id="element-238">
                  Positif
                </span>
              </button>
              <button type="button" onClick={() => setFeedbackType('constructive')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${feedbackType === 'constructive' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`} data-id="element-239">
                <ThumbsDown className={`h-5 w-5 mx-auto mb-1 ${feedbackType === 'constructive' ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-240" />
                <span className={`text-sm font-medium ${feedbackType === 'constructive' ? 'text-amber-900' : 'text-gray-600'}`} data-id="element-241">
                  Constructif
                </span>
              </button>
            </div>
          </div>

          <div data-id="element-242">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-243">
              Votre message
            </label>
            <textarea value={feedbackMessage} onChange={e => setFeedbackMessage(e.target.value)} placeholder="Décrivez votre feedback de manière claire et respectueuse..." rows={5} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" required data-id="element-244" />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-245">
            <button type="button" onClick={() => setIsFeedbackModalOpen(false)} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-246">
              Annuler
            </button>
            <button type="submit" className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-247">
              <Send className="h-4 w-4" data-id="element-248" />
              Envoyer pour validation
            </button>
          </div>
        </form>
      </Modal>

      {/* Feedback Request Modal */}
      <Modal isOpen={isFeedbackRequestModalOpen} onClose={() => setIsFeedbackRequestModalOpen(false)} title="Demander du feedback" size="md" data-id="element-249">
        <form onSubmit={handleFeedbackRequestSubmit} className="space-y-4" data-id="element-250">
          <div data-id="element-251">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-252">
              À qui souhaitez-vous demander du feedback?
            </label>
            <select className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors" required data-id="element-253">
              <option value="" data-id="element-254">Sélectionner une personne...</option>
              <option data-id="element-255">Jean Martin (Manager)</option>
              <option data-id="element-256">Marie Dupont</option>
              <option data-id="element-257">Thomas Petit</option>
              <option data-id="element-258">Sophie Bernard</option>
            </select>
          </div>
          <div data-id="element-259">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-260">
              Sur quel sujet souhaitez-vous recevoir du feedback?
            </label>
            <select className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors" required data-id="element-261">
              <option value="" data-id="element-262">Sélectionner un sujet...</option>
              <option data-id="element-263">Compétences techniques</option>
              <option data-id="element-264">Communication</option>
              <option data-id="element-265">Leadership</option>
              <option data-id="element-266">Travail d'équipe</option>
              <option data-id="element-267">Projet spécifique</option>
              <option data-id="element-268">Performance globale</option>
              <option data-id="element-269">Autre</option>
            </select>
          </div>
          <div data-id="element-270">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-271">
              Message (optionnel)
            </label>
            <textarea rows={4} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-colors resize-none" placeholder="Ajoutez des détails sur ce que vous aimeriez améliorer ou sur un projet spécifique..." data-id="element-272" />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-273">
            <p className="text-xs text-blue-800 leading-relaxed" data-id="element-274">
              💡 Conseil: Soyez spécifique dans votre demande pour obtenir un
              feedback plus utile et actionnable.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4" data-id="element-275">
            <button type="button" onClick={() => setIsFeedbackRequestModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-276">
              Annuler
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-277">
              Envoyer la demande
            </button>
          </div>
        </form>
      </Modal>
    </div>;
}